import React, { FC, useState } from 'react';
import { GeoMatchStatusEnum } from '@vatos-pas/common';
import { Point } from 'geojson';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import LocationOn from '@material-ui/icons/LocationOn';
import LocationOff from '@material-ui/icons/LocationOff';
import NotListedLocation from '@material-ui/icons/NotListedLocation';
import Warning from '@material-ui/icons/Warning';
import { makeStyles } from '@material-ui/core';
import LocationDetailDialog from './LocationDetailDialog';

export type LocationStatusIconProps = {
  variant: 'logged' | 'approved';
  timestamp: Date | string | null;
  geo: Point | null;
  status: GeoMatchStatusEnum | null;
  distance: number | null;
  userName: string | null;
  address: string;
  aggregateStatus?: GeoMatchStatusEnum | null;
  jobGeo?: Point | null;
};

const LocationStatusIcon: FC<LocationStatusIconProps> = ({
  variant,
  timestamp,
  geo,
  status,
  distance,
  userName,
  address,
  aggregateStatus,
  jobGeo,
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);

  let icon: React.ReactElement;
  let tooltip: string;

  const isJobGeoMissing =
    status === GeoMatchStatusEnum.JOB_GEOLOCATION_NOT_FILLED ||
    (status == null &&
      aggregateStatus === GeoMatchStatusEnum.JOB_GEOLOCATION_NOT_FILLED);

  // Triangle surfaces the job-level address issue even when the row was
  // never logged/approved (legacy rows backfilled to JOB_GEOLOCATION_NOT_FILLED).
  if (!timestamp && !isJobGeoMissing) return null;

  if (isJobGeoMissing) {
    icon = <Warning className={classes.black} />;
    tooltip = 'Job site address not geocoded';
  } else if (!geo) {
    icon = <NotListedLocation className={classes.yellow} />;
    tooltip = 'Location not recorded.';
  } else if (status === GeoMatchStatusEnum.MATCH) {
    icon = <LocationOn className={classes.green} />;
    tooltip = 'Location verified (within Threshold)';
  } else if (status === GeoMatchStatusEnum.MISMATCH) {
    icon = <LocationOff className={classes.red} />;
    tooltip = 'Location out of bounds (Outside Threshold)';
  } else {
    return null;
  }

  return (
    <>
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            disabled={isJobGeoMissing}
            onClick={isJobGeoMissing ? undefined : () => setDialogOpen(true)}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
      {!isJobGeoMissing && (
        <LocationDetailDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          variant={variant}
          geo={geo}
          status={status}
          distance={distance}
          userName={userName}
          address={address}
          jobGeo={jobGeo}
        />
      )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  yellow: { color: theme.palette.warning.main },
  green: { color: theme.palette.success.main },
  red: { color: theme.palette.error.main },
  black: { color: theme.palette.text.primary },
}));

export default LocationStatusIcon;
